import { AlertCircle, type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';

import { AdminButton } from '@apps/web/features/admin_design/admin_controls';
import { cn } from '@apps/web/lib/utils';

/*
 * Empty and error states — Linear-tight editorial.
 *
 * Round 4: monochrome throughout. Both states share a 32px
 * ink-quaternary icon, a 14px ink-secondary heading, a 12px ink-muted
 * subcopy, and at most one secondary CTA. Danger red is deliberately
 * absent — an alert icon is the semantic; color would double the
 * signal and clash with the ink-on-paper aesthetic.
 */

export function AdminEmptyState({
    icon: Icon,
    title,
    description,
    action,
    className,
    ...props
}: React.ComponentProps<'div'> & {
    readonly icon?: LucideIcon;
    readonly title: ReactNode;
    readonly description?: ReactNode;
    readonly action?: ReactNode;
}) {
    return (
        <div
            data-slot="admin-empty-state"
            className={cn(
                'flex flex-col items-center justify-center px-6 py-16 text-center',
                className,
            )}
            {...props}
        >
            {Icon !== undefined && (
                <Icon
                    aria-hidden="true"
                    data-testid="admin-empty-state-icon"
                    strokeWidth={1.5}
                    className="text-admin-ink-faint mb-3 size-8"
                />
            )}
            <div className="text-admin-lg text-admin-ink-secondary font-medium">
                {title}
            </div>
            {description !== undefined && (
                <p className="text-admin-sm text-admin-ink-muted mt-1 max-w-sm text-pretty">
                    {description}
                </p>
            )}
            {action !== undefined && <div className="mt-4">{action}</div>}
        </div>
    );
}

export function AdminErrorState({
    title = 'Something went wrong',
    description,
    onRetry,
    className,
    ...props
}: React.ComponentProps<'div'> & {
    readonly title?: ReactNode;
    readonly description?: ReactNode;
    readonly onRetry?: () => void;
}) {
    return (
        <div
            data-slot="admin-error-state"
            role="alert"
            className={cn(
                'flex flex-col items-center justify-center px-6 py-16 text-center',
                className,
            )}
            {...props}
        >
            <AlertCircle
                aria-hidden="true"
                data-testid="admin-error-state-icon"
                strokeWidth={1.5}
                className="text-admin-ink-faint mb-3 size-8"
            />
            <div className="text-admin-lg text-admin-ink-secondary font-medium">
                {title}
            </div>
            {description !== undefined && (
                <p className="text-admin-sm text-admin-ink-muted mt-1 max-w-sm text-pretty">
                    {description}
                </p>
            )}
            {onRetry !== undefined && (
                <div className="mt-4">
                    <AdminButton
                        variant="secondary"
                        size="sm"
                        onClick={onRetry}
                    >
                        Retry
                    </AdminButton>
                </div>
            )}
        </div>
    );
}
